import { useEffect, useState, useMemo } from 'react';
import { DealUIObjectWithBrand } from './DealsSection';
import { DealsSortBy, filterState } from './state';
import { getTopDeals } from 'common/api/deals/getTopDeals';
import { getBrandsFromDealIds } from 'common/api/constructor/getBrandsFromDealIds';
import { Brand } from 'common/features/store/duck/search-generic/utils/searchResultBrandsToBrands';
import { useConstructorDefaultParameters } from 'common/api/constructor/useConstructorDefaultParameters';

const uniqId = (id: string, index: number, array: string[]) => array.indexOf(id) === index;

const dealTypeMap = {
    'Online Deal': 'deal',
    'Online Coupon': 'coupon',
};

export function useDealsForFilterState(state: filterState) {
    const [allDeals, setAllDeals] = useState<DealUIObjectWithBrand[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentDeals, setCurrentDeals] = useState<DealUIObjectWithBrand[]>([]);
    const defaultFilters = useConstructorDefaultParameters({});

    useEffect(() => {
        setLoading(true);

        if (!defaultFilters.current) {
            return;
        }

        getTopDeals(100).then((deals) => {
            // setAllDeals(deals);
            const ids = deals.map((deal) => deal.affiliateId).filter(uniqId);
            getBrandsFromDealIds(ids, defaultFilters.current).then((brands: any[]) => {
                const indexedByBrandId = brands.reduce<Record<string, Brand>>((acc, brand) => {
                    acc[brand.affiliateBrandId] = brand;
                    return acc;
                }, {});

                const nextDeals = deals
                    .map((deal) => ({
                        ...deal,
                        brand: indexedByBrandId[deal.affiliateId],
                    }))
                    .filter((deal) => deal.brand);

                setAllDeals(() => nextDeals);
                setLoading(() => false);
            });
        });
    }, [JSON.stringify(defaultFilters.current)]);

    useEffect(() => {
        const mappedDealType = state.dealTypes.map((dt) => dealTypeMap[dt]);
        const startsWithNumberRegex = /$[0-9]/;

        const nextDeals = allDeals.filter((deal) => {
            const brand = deal.brand;

            if (!brand) {
                return false;
            }

            if (state.viewBonusOnly && !brand.affiliateBonus) {
                return false;
            }

            if (state.dealTypes.length > 0 && !mappedDealType.includes(deal.dealType)) {
                return false;
            }

            const brandCategories = brand.categories;
            const filteredCategories = state.categories;

            if (
                filteredCategories.length > 0 &&
                !filteredCategories.some((category) => brandCategories.includes(category))
            ) {
                return false;
            }

            if (state.letterFilter === '#') {
                if (!startsWithNumberRegex.test(brand.name)) {
                    return false;
                }
            } else if (
                state.letterFilter !== 'All' &&
                state.letterFilter &&
                !brand.name.toUpperCase().startsWith(state.letterFilter.toUpperCase())
            ) {
                return false;
            }

            return true;
        });

        nextDeals.sort((a: DealUIObjectWithBrand, b: DealUIObjectWithBrand) => {
            switch (state.sortBy.value) {
                case DealsSortBy.EndingSoon:
                    return new Date(a.endAt).getTime() - new Date(b.endAt).getTime();
                case DealsSortBy.Newest:
                    return new Date(b.startAt).getTime() - new Date(a.startAt).getTime();
                case DealsSortBy.MostPopular:
                    return b.sortScore - a.sortScore;
                case DealsSortBy.MaxRebate:
                    return (
                        Math.max(b.brand?.affiliateBonus || -Infinity, b.brand?.affiliateRebate) -
                        Math.max(a.brand?.affiliateBonus || -Infinity, a.brand?.affiliateRebate)
                    );
                case DealsSortBy.NameAsc:
                    return a.brand.name.localeCompare(b.brand.name);
                case DealsSortBy.NameDesc:
                    return b.brand.name.localeCompare(a.brand.name);
                default:
                    return 0;
            }
        });

        setCurrentDeals(() => nextDeals);
    }, [
        allDeals,
        state.dealTypes,
        state.categories,
        state.letterFilter,
        state.viewBonusOnly,
        state.sortBy,
    ]);

    const api = useMemo(
        () => ({
            allDeals,
            deals: currentDeals,
            loading,
        }),
        [allDeals, currentDeals, loading]
    );

    return api;
}
